import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { getComponentTheme } from "@/lib/theme";

const buttonTheme = getComponentTheme('button');

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: `${buttonTheme.default.background} ${buttonTheme.default.text} ${buttonTheme.default.hover}`,
        destructive: `${buttonTheme.destructive.background} ${buttonTheme.destructive.text} ${buttonTheme.destructive.hover}`,
        outline: `border ${buttonTheme.outline.border} ${buttonTheme.outline.background} ${buttonTheme.outline.text} ${buttonTheme.outline.hover}`,
        navOutline: `border ${buttonTheme.navOutline.border} ${buttonTheme.navOutline.background} ${buttonTheme.navOutline.text} ${buttonTheme.navOutline.hover}`,
        secondary: `${buttonTheme.secondary.background} ${buttonTheme.secondary.text} ${buttonTheme.secondary.hover}`,
        ghost: `${buttonTheme.ghost.background} ${buttonTheme.ghost.text} ${buttonTheme.ghost.hover}`,
        navGhost: `${buttonTheme.navGhost.background} ${buttonTheme.navGhost.text} ${buttonTheme.navGhost.hover}`,
        link: `${buttonTheme.link.text} ${buttonTheme.link.decoration}`,
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
